import { useState, useEffect } from "react";
import { Plug, Key, Webhook, Database, Mail, Copy, CheckCircle2 } from "lucide-react";
import api from "../../../api";
import { useModal } from "../../../context/ModalContext";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

export default function Integrations() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatedKey, setGeneratedKey] = useState(null);
  const [newKeyName, setNewKeyName] = useState("");
  const [copied, setCopied] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchKeys();
  }, []);

  const fetchKeys = async () => {
    try {
      const res = await api.get("/integrations/keys");
      setKeys(res.data);
    } catch (err) {
      console.error("Erreur de chargement des clés", err);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateKey = async () => {
    if (!newKeyName.trim()) return;
    try {
      const res = await api.post("/integrations/keys", {
        name: newKeyName,
        service: "Custom API"
      });
      setGeneratedKey(res.data.key);
      fetchKeys();
    } catch (err) {
      console.error("Erreur génération clé", err);
    }
  };

  const { confirm } = useModal();

  const handleRevokeKey = async (id) => {
    const isConfirmed = await confirm({
      title: "Révoquer la clé API",
      message: "Êtes-vous sûr de vouloir révoquer cette clé ? Elle ne fonctionnera plus.",
      type: "danger",
      confirmText: "Révoquer",
      cancelText: "Annuler"
    });
    if (!isConfirmed) return;
    try {
      await api.delete(`/integrations/keys/${id}`);
      fetchKeys();
    } catch (err) {
      console.error("Erreur révocation clé", err);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const integrations = [
    {
      id: "flows",
      name: "FlowS",
      description: "Webhook de réception de Leads depuis FlowS.",
      icon: <Webhook className="w-6 h-6 text-pink-500" />,
      status: keys.length > 0 ? "connected" : "disconnected",
      lastSync: null,
    },
    {
      id: "brevo",
      name: "Brevo (ex-Sendinblue)",
      description: "Synchronisation des contacts et envoi de campagnes email/SMS.",
      icon: <Mail className="w-6 h-6 text-info-500" />,
      status: "disconnected",
      lastSync: null,
    },
    {
      id: "quantalys",
      name: "Quantalys",
      description: "Import automatique des valorisations et données de marché.",
      icon: <Database className="w-6 h-6 text-purple-500" />,
      status: "disconnected",
      lastSync: null,
    }
  ];

  return (
    <div className="p-6 h-full flex flex-col gap-6 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-transparent relative">
      <div className="mb-2">
        <h2 className="text-2xl font-bold text-primary flex items-center gap-2">
          <Plug className="w-6 h-6 text-info-500" />
          Intégrations & API
        </h2>
        <p className="text-sm text-subtle mt-1">Connectez Hipparchus à vos outils favoris et gérez vos clés d'API.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {integrations.map((integration) => (
          <div key={integration.id} className="workspace-card p-6 flex flex-col relative overflow-hidden group hover:border-primary-500 transition-colors">
            <div className="flex justify-between items-start mb-4">
              <div className="w-12 h-12 rounded-xl bg-surface-muted border border-workspace flex items-center justify-center shrink-0">
                {integration.icon}
              </div>
              {integration.status === "connected" ? (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-success-100 text-success-800 dark:bg-success-900/30 dark:text-success-300">
                  Connecté
                </span>
              ) : (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-ink-100 text-ink-800 dark:bg-ink-800 dark:text-ink-300">
                  Déconnecté
                </span>
              )}
            </div>
            
            <h3 className="text-lg font-bold text-primary mb-2">{integration.name}</h3>
            <p className="text-sm text-subtle flex-1">{integration.description}</p>
            
            <div className="mt-6 pt-4 border-t border-workspace flex items-center justify-between">
              <span className="text-xs text-subtle">
                {integration.lastSync ? `Dernière sync. : ${integration.lastSync}` : "Aucune synchronisation"}
              </span>
              <button className={`text-sm font-medium ${integration.status === "connected" ? "text-primary hover:text-danger-500" : "text-primary hover:text-info-500"}`}>
                {integration.status === "connected" ? "Gérer" : "Connecter"}
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="workspace-card p-6 mt-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h3 className="text-lg font-semibold text-primary flex items-center gap-2">
              <Key className="w-5 h-5 text-warning-500" />
              Clés API
            </h3>
            <p className="text-sm text-subtle mt-1">Créez des jetons d'accès pour interagir avec l'API Hipparchus.</p>
          </div>
          <button onClick={() => setShowModal(true)} className="btn btn-primary">Générer une clé API</button>
        </div>

        <div className="overflow-x-auto">
          {loading ? (
            <div className="text-center p-4 text-subtle">Chargement des clés...</div>
          ) : (
            <table className="w-full text-left border-collapse">
              <thead className="bg-workspace">
                <tr>
                  <th className="px-4 py-3 text-xs font-semibold text-subtle uppercase border-b border-workspace">Nom de la clé</th>
                  <th className="px-4 py-3 text-xs font-semibold text-subtle uppercase border-b border-workspace">Créée le</th>
                  <th className="px-4 py-3 text-xs font-semibold text-subtle uppercase border-b border-workspace">Dernière utilisation</th>
                  <th className="px-4 py-3 text-xs font-semibold text-subtle uppercase border-b border-workspace">Statut</th>
                  <th className="px-4 py-3 text-xs font-semibold text-subtle uppercase border-b border-workspace text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-workspace">
                {keys.map((key) => (
                  <tr key={key.id} className="hover:bg-workspace/50 transition-colors">
                    <td className="px-4 py-4 text-sm font-medium text-primary">{key.name}</td>
                    <td className="px-4 py-4 text-sm text-subtle">
                      {format(new Date(key.createdAt), "dd/MM/yyyy HH:mm", { locale: fr })}
                    </td>
                    <td className="px-4 py-4 text-sm text-subtle">
                      {key.lastUsedAt ? format(new Date(key.lastUsedAt), "dd/MM/yyyy HH:mm", { locale: fr }) : "Jamais"}
                    </td>
                    <td className="px-4 py-4">
                      {key.revoked ? (
                        <span className="text-xs font-medium bg-danger-100 text-danger-800 dark:bg-danger-900/30 dark:text-danger-300 px-2 py-1 rounded-md">Révoquée</span>
                      ) : (
                        <span className="text-xs font-medium bg-success-100 text-success-800 dark:bg-success-900/30 dark:text-success-300 px-2 py-1 rounded-md">Active</span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-right">
                      {!key.revoked && (
                        <button onClick={() => handleRevokeKey(key.id)} className="text-danger-500 hover:text-danger-700 text-sm font-medium">Révoquer</button>
                      )}
                    </td>
                  </tr>
                ))}
                {keys.length === 0 && (
                  <tr>
                    <td colSpan="5" className="px-4 py-8 text-center text-subtle text-sm">Aucune clé API n'a été générée.</td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Modal Génération Clé */}
      {showModal && (
        <div className="fixed inset-0 z-[100] bg-ink-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-surface rounded-xl shadow-hp-xl max-w-md w-full border border-workspace p-6">
            <h3 className="text-lg font-bold text-primary mb-4">Générer une clé API</h3>
            
            {generatedKey ? (
              <div>
                <div className="bg-warning-50 dark:bg-warning-900/20 text-warning-800 dark:text-warning-200 p-4 rounded-lg mb-4 text-sm border border-warning-100 dark:border-warning-800/50">
                  <p className="font-semibold mb-1">Attention !</p>
                  <p>Copiez cette clé immédiatement. Elle ne sera plus jamais affichée.</p>
                </div>
                
                <div className="flex items-center gap-2 mb-6">
                  <input type="text" readOnly value={generatedKey} className="workspace-input flex-1 font-mono text-sm" />
                  <button onClick={copyToClipboard} className="btn btn-secondary p-2">
                    {copied ? <CheckCircle2 className="w-5 h-5 text-success-500" /> : <Copy className="w-5 h-5" />}
                  </button>
                </div>
                
                <button onClick={() => { setShowModal(false); setGeneratedKey(null); setNewKeyName(""); }} className="btn btn-primary w-full justify-center">
                  J'ai copié ma clé
                </button>
              </div>
            ) : (
              <div>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-primary mb-1">Nom de la clé (Ex: FlowS Prod)</label>
                  <input 
                    type="text" 
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                    className="workspace-input w-full"
                    placeholder="Mon application..."
                    autoFocus
                  />
                </div>
                <div className="flex gap-3 justify-end">
                  <button onClick={() => setShowModal(false)} className="btn btn-secondary">Annuler</button>
                  <button onClick={handleGenerateKey} disabled={!newKeyName.trim()} className="btn btn-primary">Générer</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
